import { useId, useMemo, useRef, useState } from "react";
import { rupiah } from "./OrderSummaryCard";
import { groupOrderItems, pickOrderValue as pick } from "./orderDisplayHelpers";

const PREVIEW_COUNT = 5;

const STATUSES = ["DAPAT", "ORDER", "SOLD", "CANCEL"];
const TABS = [{ key: "ALL", label: "Semua" }, ...STATUSES.map((s) => ({ key: s, label: s }))];

const badge = {
    DAPAT: "bg-emerald-50 text-emerald-700 border-emerald-200",
    ORDER: "bg-[color:var(--wx-blue-soft)] text-[color:var(--wx-blue)] border-blue-200",
    SOLD: "bg-red-50 text-red-600 border-red-200",
    CANCEL: "bg-gray-100 text-gray-500 border-gray-200",
};

export default function OrderItemList({ orders }) {
    const [tab, setTab] = useState("ALL");
    const [expanded, setExpanded] = useState(false);
    const listId = useId();
    const toggleRef = useRef(null);
    const items = useMemo(
        () => groupOrderItems(orders),
        [orders],
    );
    const counts = useMemo(() => {
        const c = { ALL: items.length, DAPAT: 0, ORDER: 0, SOLD: 0, CANCEL: 0 };
        items.forEach((it) => { if (c[it._status] !== undefined) c[it._status] += 1; });
        return c;
    }, [items]);
    const filtered = tab === "ALL" ? items : items.filter((i) => i._status === tab);
    const visibleItems = expanded ? filtered : filtered.slice(0, PREVIEW_COUNT);
    const canExpand = filtered.length > PREVIEW_COUNT;

    return (
        <section className="mt-4">
            <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-[color:var(--wx-ink)]">Barang pesanan</h2>
                <span className="text-[11px] text-[color:var(--wx-muted)]">{items.length} jenis</span>
            </div>

            <div data-testid="status-tabs" className="mt-2 flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1">
                {TABS.map((t) => {
                    const active = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            type="button"
                            data-testid={`tab-${t.key}`}
                            onClick={() => {
                                if (tab !== t.key) {
                                    setTab(t.key);
                                    setExpanded(false);
                                }
                            }}
                            aria-pressed={active}
                            className={`shrink-0 rounded-full px-3 py-1.5 text-[11px] font-semibold border transition-colors ${
                                active
                                    ? "bg-[color:var(--wx-ink)] text-white border-[color:var(--wx-ink)]"
                                    : "bg-white text-[color:var(--wx-muted)] border-[color:var(--wx-line)] hover:text-[color:var(--wx-ink)]"
                            }`}
                        >
                            {t.label}
                            <span className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-4 rounded-full text-[9px] px-1 ${
                                active ? "bg-white/20 text-white" : "bg-[color:var(--wx-surface)] text-[color:var(--wx-ink-2)]"
                            }`}>
                                {counts[t.key]}
                            </span>
                        </button>
                    );
                })}
            </div>

            {expanded && canExpand && (
                <div className="mt-2 flex justify-end">
                    <button
                        type="button"
                        data-testid="collapse-order-items-top"
                        aria-expanded="true"
                        aria-controls={listId}
                        onClick={() => {
                            setExpanded(false);
                            toggleRef.current?.focus();
                        }}
                        className="rounded-md px-2 py-1 text-[11px] font-semibold text-[color:var(--wx-blue)] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--wx-blue)]"
                    >
                        Tampilkan lebih sedikit
                    </button>
                </div>
            )}

            <ul id={listId} data-testid="order-items" className="mt-2 wx-card divide-y divide-[color:var(--wx-line)] overflow-hidden">
                {visibleItems.map((it) => {
                    const name = pick(it, "item", "name", "nama", "product", "barang") || "-";
                    const color = pick(it, "color", "warna");
                    const size = pick(it, "size", "ukuran");
                    const qty = it.quantity_valid === false ? "" : it._quantity ?? pick(it, "qty", "quantity", "jumlah");
                    const price = it._subtotal;
                    const variant = pick(it, "variant", "varian", "Variant");
                    const meta = [variant, color, size, qty !== "" ? `${qty} pcs` : ""].filter(Boolean).join(" · ");
                    return (
                        <li key={it._i} data-testid={`order-item-${it._i}`} className="px-3 py-2.5 flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                                <p className="text-[13px] font-semibold text-[color:var(--wx-ink)] leading-snug line-clamp-2">{name}</p>
                                {meta && <p className="mt-0.5 text-[11px] text-[color:var(--wx-muted)] truncate">{meta}</p>}
                            </div>
                            <div className="shrink-0 flex flex-col items-end gap-1">
                                <span
                                    data-testid={`status-badge-${it._status}`}
                                    className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide border ${badge[it._status] || badge.ORDER}`}
                                >
                                    {it._status}
                                </span>
                                {price !== null && (
                                    <p className="text-[12px] font-bold text-[color:var(--wx-ink)]">
                                        {rupiah(price)}
                                    </p>
                                )}
                            </div>
                        </li>
                    );
                })}
                {filtered.length === 0 && (
                    <li data-testid="empty-state" className="p-6 text-center text-xs text-[color:var(--wx-muted)]">
                        {items.length === 0 ? "Belum ada barang di pesanan ini." : "Tidak ada barang dengan status ini."}
                    </li>
                )}
            </ul>
            {filtered.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <p data-testid="visible-item-count" className="text-[11px] text-[color:var(--wx-muted)]" aria-live="polite">
                        Menampilkan {visibleItems.length} dari {filtered.length} jenis{tab === "ALL" ? "" : ` ${tab}`}
                    </p>
                    {canExpand && (
                        <button
                            type="button"
                            data-testid="toggle-order-items"
                            ref={toggleRef}
                            aria-expanded={expanded}
                            aria-controls={listId}
                            onClick={() => setExpanded((value) => !value)}
                            className="rounded-lg border border-[color:var(--wx-line)] bg-white px-3 py-2 text-[11px] font-semibold text-[color:var(--wx-blue)] transition-colors hover:bg-[color:var(--wx-surface)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--wx-blue)]"
                        >
                            {expanded ? "Tampilkan lebih sedikit" : tab === "ALL"
                                ? `Lihat semua pesanan (${filtered.length})`
                                : `Lihat semua barang ${tab} (${filtered.length})`}
                        </button>
                    )}
                </div>
            )}
        </section>
    );
}

